import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, CheckCircle, Clock, Package, Truck, Home, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackingPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tracking, setTracking] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const guestBookingToken = localStorage.getItem(`guest_booking_${bookingId}`);
    if (!user && !guestBookingToken) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [trackingRes, bookingRes] = await Promise.all([
          axios.get(`${API}/prasad-tracking/${bookingId}`, { withCredentials: true, headers: guestBookingToken ? { 'X-Guest-Booking-Token': guestBookingToken } : {} }),
          axios.get(`${API}/bookings/${bookingId}`, { withCredentials: true, headers: guestBookingToken ? { 'X-Guest-Booking-Token': guestBookingToken } : {} })
        ]);
        setTracking(trackingRes.data);
        setBooking(bookingRes.data);
      } catch (error) {
        console.error('Error fetching tracking:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bookingId, user, navigate]);

  const steps = [
    { id: 'puja_scheduled', label: 'Puja Scheduled', icon: Clock, description: 'Your puja has been scheduled' },
    { id: 'puja_completed', label: 'Puja Completed', icon: CheckCircle, description: 'Puja performed by Vedic Brahmins' },
    { id: 'offering_prepared', label: 'Prasad Prepared', icon: Package, description: 'Sacred prasad packaged with care' },
    { id: 'courier_picked', label: 'Courier Picked', icon: Truck, description: 'Prasad handed to delivery partner' },
    { id: 'in_transit', label: 'In Transit', icon: MapPin, description: 'On the way to your location' },
    { id: 'delivered', label: 'Delivered', icon: Home, description: 'Prasad delivered successfully' },
  ];

  const getCurrentStepIndex = () => {
    if (!tracking) return 0;
    return steps.findIndex(step => step.id === tracking.status);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const currentStepIndex = getCurrentStepIndex();

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#FAFBFC] pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-[#4A6274] hover:text-[#FF8C00] mb-6 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{user ? 'Back to Dashboard' : 'Back'}</span>
          </button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 mb-8"
          >
            <div className="flex items-center gap-4">
              <img
                src={booking?.puja?.image_url || booking?.temple?.image_url}
                alt={booking?.puja?.name}
                className="w-20 h-20 rounded-xl object-cover"
              />
              <div>
                <h1 className="font-heading text-xl font-bold text-[#1A202C]">
                  {booking?.puja?.name}
                </h1>
                <p className="text-[#718096]">{booking?.temple?.name}</p>
                <p className="text-sm text-[#4A5568]">Devotee(s): {booking?.devotee_names?.join(', ')}</p>
              </div>
            </div>
          </motion.div>

          {/* Tracking Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"
          >
            <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-8">
              Prasad Tracking
            </h2>

            <div className="tracking-timeline">
              {steps.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                
                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="tracking-step"
                    data-testid={`tracking-step-${step.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Dot */}
                      <div className={`tracking-dot ${isCompleted ? 'completed' : 'pending'} ${isCurrent ? 'pulse-glow' : ''}`}>
                        {isCompleted && !isCurrent && (
                          <CheckCircle className="absolute -top-1 -left-1 w-6 h-6 text-green-500 bg-white rounded-full" />
                        )}
                      </div>

                      {/* Content */}
                      <div className={`flex-1 ${!isCompleted ? 'opacity-50' : ''}`}>
                        <div className="flex items-center gap-3 mb-1">
                          <step.icon className={`w-5 h-5 ${isCompleted ? 'text-[#FF8C00]' : 'text-gray-400'}`} />
                          <h3 className={`font-semibold ${isCompleted ? 'text-[#1A202C]' : 'text-gray-400'}`}>
                            {step.label}
                          </h3>
                          {isCurrent && (
                            <span className="px-2 py-0.5 bg-[#FF8C00]/10 text-[#FF8C00] text-xs rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className={`text-sm ${isCompleted ? 'text-[#718096]' : 'text-gray-300'}`}>
                          {step.description}
                        </p>
                        
                        {/* Timestamp from updates */}
                        {tracking?.updates?.find(u => u.status === step.id) && (
                          <p className="text-xs text-[#718096] mt-1">
                            {new Date(tracking.updates.find(u => u.status === step.id).timestamp).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Tracking Number */}
            {tracking?.tracking_number && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-sm text-[#718096]">Tracking Number</p>
                <p className="font-mono text-lg text-[#1A202C]">{tracking.tracking_number}</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TrackingPage;
